import { useState, useEffect } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, configDB } from '../services/db'

const TIPUS = ['peu', 'transport', 'híbrida']
const TIPUS_EMOJI = { peu: '🚶', transport: '🚌', híbrida: '🔀' }

const DIFICULTATS = [
  { key: 'molt_baixa', label: 'Molt Baixa', emoji: '🟢' },
  { key: 'baixa',      label: 'Baixa',      emoji: '🟡' },
  { key: 'normal',     label: 'Normal',     emoji: '⚪' },
  { key: 'alta',       label: 'Alta',       emoji: '🟠' },
  { key: 'molt_alta',  label: 'Molt Alta',  emoji: '🔴' },
]

const TEMPS_ATENCIO_DEFAULT = { molt_baixa: 15, baixa: 20, normal: 30, alta: 45, molt_alta: 60 }

const INICI_TIPUS = ['casa', 'hospital', 'custom']
const INICI_LABEL = { casa: '🏠 Casa', hospital: '🏥 Hospital', custom: '📌 Personalitzat' }

async function geocodificar(adreca) {
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(adreca)}&limit=1&countrycodes=es`
  const res  = await fetch(url, {
    headers: { 'Accept-Language': 'ca,es', 'User-Agent': 'rutes-hado-app' },
  })
  const data = await res.json()
  if (!data.length) throw new Error('Adreça no trobada')
  return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }
}

export default function SettingsPage() {
  const config = useLiveQuery(() => configDB.getOrDefault(), [])

  const [form, setForm]   = useState({
    hora_inici:     '09:00',
    tipus_ruta:     'peu',
    num_slots:      40,
    temps_atencio:  { ...TEMPS_ATENCIO_DEFAULT },
    temps_ingres:   20,
    temps_alta:     15,
    ubicacio_inici: { tipus: 'casa', adreca: '', lat: null, lng: null },
    hospital:       { nom: '', adreca: '', lat: null, lng: null, dificultat: 'normal' },
  })
  const [desat, setDesat]               = useState(false)
  const [geocodificantInici, setGeocodificantInici] = useState(false)
  const [geocodificantHosp,  setGeocodificantHosp]  = useState(false)
  const [geoError, setGeoError]         = useState(null)
  const [modalEliminacio, setModalEliminacio] = useState(false)
  const [textConfirmacio, setTextConfirmacio] = useState('')

  useEffect(() => {
    if (config) setForm({
      hora_inici:     config.hora_inici     ?? '09:00',
      tipus_ruta:     config.tipus_ruta     ?? 'peu',
      num_slots:      config.num_slots      ?? 40,
      temps_atencio:  { ...TEMPS_ATENCIO_DEFAULT, ...(config.temps_atencio ?? {}) },
      temps_ingres:   config.temps_ingres   ?? 20,
      temps_alta:     config.temps_alta     ?? 15,
      ubicacio_inici: config.ubicacio_inici ?? { tipus: 'casa', adreca: '', lat: null, lng: null },
      hospital:       { nom: '', adreca: '', lat: null, lng: null, dificultat: 'normal', ...(config.hospital ?? {}) },
    })
  }, [config])

  function setF(key, val) { setForm(f => ({ ...f, [key]: val })) }
  function setUbicacio(key, val) { setForm(f => ({ ...f, ubicacio_inici: { ...f.ubicacio_inici, [key]: val } })) }
  function setHospital(key, val) { setForm(f => ({ ...f, hospital: { ...f.hospital, [key]: val } })) }

  function handleTipusInici(nouTipus) {
    if (nouTipus === 'hospital' && form.hospital?.adreca) {
      setForm(f => ({
        ...f,
        ubicacio_inici: {
          tipus: 'hospital',
          adreca: f.hospital.adreca,
          lat:    f.hospital.lat,
          lng:    f.hospital.lng,
        },
      }))
    } else {
      setUbicacio('tipus', nouTipus)
    }
  }

  async function geocodificarInici() {
    if (!form.ubicacio_inici.adreca) return
    setGeocodificantInici(true)
    setGeoError(null)
    try {
      const { lat, lng } = await geocodificar(form.ubicacio_inici.adreca)
      setUbicacio('lat', lat)
      setUbicacio('lng', lng)
    } catch {
      setGeoError('inici')
    } finally {
      setGeocodificantInici(false)
    }
  }

  async function geocodificarHospital() {
    if (!form.hospital.adreca) return
    setGeocodificantHosp(true)
    setGeoError(null)
    try {
      const { lat, lng } = await geocodificar(form.hospital.adreca)
      setHospital('lat', lat)
      setHospital('lng', lng)
    } catch {
      setGeoError('hospital')
    } finally {
      setGeocodificantHosp(false)
    }
  }

  async function handleEliminarTotesLesDades() {
    if (textConfirmacio !== 'ELIMINAR') return
    try {
      await Promise.all([
        db.domicilis.clear(),
        db.rutes.clear(),
        db.anotacions.clear(),
        db.config.clear(),
      ])
      window.location.reload()
    } catch (err) {
      console.error('[Eliminar] Error:', err)
      alert('❌ Error en eliminar les dades. Torna a provar.')
    }
  }

  async function guardar() {
    let formFinal = { ...form }

    // Auto-geocodifica l'hospital si té adreça però no coordenades
    if (form.hospital.adreca && !form.hospital.lat) {
      try {
        const { lat, lng } = await geocodificar(form.hospital.adreca)
        formFinal = { ...formFinal, hospital: { ...formFinal.hospital, lat, lng } }
        setHospital('lat', lat)
        setHospital('lng', lng)
      } catch { /* no bloqueja el guardat */ }
    }

    // Auto-geocodifica la ubicació d'inici si té adreça però no coordenades
    if (form.ubicacio_inici.adreca && !form.ubicacio_inici.lat && form.ubicacio_inici.tipus !== 'hospital') {
      try {
        const { lat, lng } = await geocodificar(form.ubicacio_inici.adreca)
        formFinal = { ...formFinal, ubicacio_inici: { ...formFinal.ubicacio_inici, lat, lng } }
        setUbicacio('lat', lat)
        setUbicacio('lng', lng)
      } catch { /* no bloqueja el guardat */ }
    }

    await configDB.update(formFinal)
    setDesat(true)
    setTimeout(() => setDesat(false), 2000)
  }

  const coordsInici = form.ubicacio_inici.lat
    ? `${form.ubicacio_inici.lat.toFixed(5)}, ${form.ubicacio_inici.lng.toFixed(5)}`
    : null
  const coordsHosp = form.hospital.lat
    ? `${form.hospital.lat.toFixed(5)}, ${form.hospital.lng.toFixed(5)}`
    : null

  return (
    <div style={S.page}>
      <header style={S.header}>
        <h1 style={S.title}>Configuració</h1>
      </header>

      <div style={S.contingut}>

        {/* Valors per defecte */}
        <div style={S.seccio}>
          <p style={S.seccioTitol}>Valors per defecte</p>
          <div style={S.bloc}>
            <div style={S.fila}>
              <span style={S.filaLabel}>Hora d'inici</span>
              <input style={S.inputInline} type="time"
                value={form.hora_inici}
                onChange={e => setF('hora_inici', e.target.value)} />
            </div>
            <div style={{ ...S.fila, borderBottom: 'none' }}>
              <span style={S.filaLabel}>Tipus de ruta</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {TIPUS.map(t => (
                  <button key={t} style={{
                    ...S.tipusBtn,
                    backgroundColor: form.tipus_ruta === t ? '#007AFF' : '#f3f4f6',
                    color:           form.tipus_ruta === t ? '#fff'    : '#374151',
                  }} onClick={() => setF('tipus_ruta', t)}>
                    {TIPUS_EMOJI[t]}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p style={S.hint}>Tipus: <strong>{TIPUS_EMOJI[form.tipus_ruta]} {form.tipus_ruta}</strong></p>
        </div>

        {/* Slots HADO */}
        <div style={S.seccio}>
          <p style={S.seccioTitol}>Slots HADO</p>
          <div style={S.bloc}>
            <div style={S.fila}>
              <span style={S.filaLabel}>Nombre de slots disponibles</span>
              <input
                type="number"
                min={1}
                max={99}
                style={{ ...S.inputInline, width: 64, textAlign: 'center' }}
                value={form.num_slots ?? 40}
                onChange={e => {
                  const val = Math.min(99, Math.max(1, parseInt(e.target.value) || 1))
                  setF('num_slots', val)
                }}
              />
            </div>
            <div style={{ ...S.fila, borderBottom: 'none' }}>
              <span style={S.filaLabel}>Slots actuals</span>
              <span style={S.filaValor}>HADO1 … HADO{form.num_slots ?? 40}</span>
            </div>
          </div>
        </div>

        {/* Ubicació d'inici */}
        <div style={S.seccio}>
          <p style={S.seccioTitol}>Ubicació d'inici</p>
          <div style={S.bloc}>
            <div style={{ ...S.fila, flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
              <span style={S.filaLabel}>Tipus d'inici</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {INICI_TIPUS.map(t => (
                  <button key={t} style={{
                    ...S.tipusBtn,
                    fontSize: 13,
                    padding: '6px 12px',
                    backgroundColor: form.ubicacio_inici.tipus === t ? '#007AFF' : '#f3f4f6',
                    color:           form.ubicacio_inici.tipus === t ? '#fff'    : '#374151',
                  }} onClick={() => handleTipusInici(t)}>
                    {INICI_LABEL[t]}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ ...S.fila, flexDirection: 'column', alignItems: 'flex-start', gap: 6, borderBottom: 'none' }}>
              <span style={S.filaLabel}>Adreça d'inici</span>
              <div style={S.geocodingFila}>
                <input style={{ ...S.inputInline, flex: 1 }}
                  placeholder="C/ Gran Via 123, Barcelona"
                  value={form.ubicacio_inici.adreca}
                  readOnly={form.ubicacio_inici.tipus === 'hospital'}
                  onChange={e => { setUbicacio('adreca', e.target.value); setUbicacio('lat', null); setUbicacio('lng', null) }} />
                <button style={S.geoBtn} onClick={geocodificarInici} disabled={geocodificantInici || !form.ubicacio_inici.adreca || form.ubicacio_inici.tipus === 'hospital'}>
                  {geocodificantInici ? '⏳' : '📍'}
                </button>
              </div>
              {form.ubicacio_inici.tipus === 'hospital' && (
                <span style={{ ...S.hint, color: '#9ca3af' }}>Sincronitzat de l'hospital de referència</span>
              )}
              {geoError === 'inici' && <span style={S.geoError}>Adreça no trobada</span>}
              {coordsInici && <span style={S.geoOk}>✓ {coordsInici}</span>}
            </div>
          </div>
        </div>

        {/* Hospital de referència */}
        <div style={S.seccio}>
          <p style={S.seccioTitol}>Hospital de referència</p>
          <div style={S.bloc}>
            <div style={{ ...S.fila, flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
              <span style={S.filaLabel}>Nom de l'hospital</span>
              <input style={{ ...S.inputInline, width: '100%', boxSizing: 'border-box' }}
                placeholder="Hospital de la Vall d'Hebron"
                value={form.hospital.nom}
                onChange={e => setHospital('nom', e.target.value)} />
            </div>
            <div style={{ ...S.fila, flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
              <span style={S.filaLabel}>Adreça</span>
              <div style={S.geocodingFila}>
                <input style={{ ...S.inputInline, flex: 1 }}
                  placeholder="Pg. Vall d'Hebron 119-129"
                  value={form.hospital.adreca}
                  onChange={e => { setHospital('adreca', e.target.value); setHospital('lat', null); setHospital('lng', null) }} />
                <button style={S.geoBtn} onClick={geocodificarHospital} disabled={geocodificantHosp || !form.hospital.adreca}>
                  {geocodificantHosp ? '⏳' : '📍'}
                </button>
              </div>
              {geoError === 'hospital' && <span style={S.geoError}>Adreça no trobada</span>}
              {coordsHosp && <span style={S.geoOk}>✓ {coordsHosp}</span>}
            </div>
            <div style={{ ...S.fila, flexDirection: 'column', alignItems: 'flex-start', gap: 6, borderBottom: 'none' }}>
              <span style={S.filaLabel}>Complexitat de la visita hospital</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DIFICULTATS.map(d => (
                  <button key={d.key} style={{
                    ...S.tipusBtn, fontSize: 12, padding: '5px 10px',
                    backgroundColor: form.hospital.dificultat === d.key ? '#FF9500' : '#f3f4f6',
                    color:           form.hospital.dificultat === d.key ? '#fff'    : '#374151',
                  }} onClick={() => setHospital('dificultat', d.key)}>
                    {d.emoji} {d.label}
                  </button>
                ))}
              </div>
              <span style={S.hint}>
                Temps estimat per visita hospital: <strong>{form.temps_atencio?.[form.hospital.dificultat ?? 'normal'] ?? 30} min</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Temps d'atenció */}
        <div style={S.seccio}>
          <p style={S.seccioTitol}>Temps d'atenció per visita</p>
          <div style={S.bloc}>
            {DIFICULTATS.map((d, i) => (
              <div key={d.key} style={{ ...S.fila, borderBottom: i < DIFICULTATS.length - 1 ? undefined : '1px solid #f3f4f6' }}>
                <span style={S.filaLabel}>{d.emoji} {d.label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="number" min={1} max={999}
                    style={{ ...S.inputInline, width: 56, textAlign: 'center' }}
                    value={form.temps_atencio?.[d.key] ?? TEMPS_ATENCIO_DEFAULT[d.key]}
                    onChange={e => {
                      const val = Math.min(999, Math.max(1, parseInt(e.target.value) || 1))
                      setF('temps_atencio', { ...form.temps_atencio, [d.key]: val })
                    }}
                  />
                  <span style={{ fontSize: 13, color: '#9ca3af' }}>min</span>
                </div>
              </div>
            ))}
            <div style={S.fila}>
              <span style={{ ...S.filaValor, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af' }}>
                Temps addicionals
              </span>
            </div>
            <div style={S.fila}>
              <span style={S.filaLabel}>🟢 Ingrés (extra)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="number" min={0} max={999}
                  style={{ ...S.inputInline, width: 56, textAlign: 'center' }}
                  value={form.temps_ingres ?? 20}
                  onChange={e => setF('temps_ingres', Math.min(999, Math.max(0, parseInt(e.target.value) || 0)))}
                />
                <span style={{ fontSize: 13, color: '#9ca3af' }}>min extra</span>
              </div>
            </div>
            <div style={{ ...S.fila, borderBottom: 'none' }}>
              <span style={S.filaLabel}>🔴 Alta (extra)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="number" min={0} max={999}
                  style={{ ...S.inputInline, width: 56, textAlign: 'center' }}
                  value={form.temps_alta ?? 15}
                  onChange={e => setF('temps_alta', Math.min(999, Math.max(0, parseInt(e.target.value) || 0)))}
                />
                <span style={{ fontSize: 13, color: '#9ca3af' }}>min extra</span>
              </div>
            </div>
          </div>
        </div>

        <button style={{ ...S.btnPrimari, opacity: desat ? 0.6 : 1 }} onClick={guardar} disabled={desat}>
          {desat ? '✓ Desat' : 'Guardar preferències'}
        </button>

        {/* Informació */}
        <div style={S.seccio}>
          <p style={S.seccioTitol}>Informació</p>
          <div style={S.bloc}>
            <InfoFila label="Versió" value="0.1.0" />
            <InfoFila label="Base de dades" value="Dexie / IndexedDB" />
            <InfoFila label="Format export" value=".hado (AES-256)" last />
          </div>
        </div>

        {/* Zona de perill */}
        <div style={S.zonaPerill}>
          <p style={S.seccioTitol}>⚠️ Zona de perill</p>
          <p style={S.periллDesc}>
            💾 Totes les dades (domicilis, rutes i configuració) estan guardades{' '}
            <strong>únicament al teu dispositiu</strong>. No hi ha còpia al núvol
            ni a cap servidor extern.
          </p>
          <button style={S.btnEliminar} onClick={() => { setModalEliminacio(true); setTextConfirmacio('') }}>
            🗑️ Eliminar totes les dades
          </button>
        </div>

      </div>

      {/* Modal de confirmació */}
      {modalEliminacio && (
        <div style={S.modalOverlay} onClick={() => setModalEliminacio(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <h3 style={S.modalTitol}>⚠️ Confirmar eliminació</h3>
            <p style={{ fontSize: 14, lineHeight: 1.6, margin: '0 0 12px' }}>
              Estàs a punt d'eliminar <strong>totes les dades</strong>:
            </p>
            <ul style={S.modalLlista}>
              <li>Tots els domicilis (HADO1…HADO{form.num_slots ?? 40})</li>
              <li>Totes les rutes creades</li>
              <li>Tota la configuració</li>
            </ul>
            <div style={S.modalAvis}>
              🚨 <strong>Aquesta acció NO és reversible.</strong>{' '}
              Les dades eliminades no es poden recuperar.
            </div>
            <label style={{ fontSize: 13, color: '#6b7280', display: 'block', margin: '16px 0 6px' }}>
              Escriu <strong>ELIMINAR</strong> per confirmar:
            </label>
            <input
              type="text"
              value={textConfirmacio}
              onChange={e => setTextConfirmacio(e.target.value)}
              placeholder="ELIMINAR"
              autoFocus
              style={{
                ...S.inputInline,
                width: '100%', boxSizing: 'border-box', fontSize: 15,
                borderColor: textConfirmacio === 'ELIMINAR' ? '#FF3B30' : '#e5e7eb',
                borderWidth: 2,
              }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button
                style={S.btnCancelModal}
                onClick={() => setModalEliminacio(false)}
              >
                Cancel·lar
              </button>
              <button
                style={{ ...S.btnConfirmarEliminar, opacity: textConfirmacio === 'ELIMINAR' ? 1 : 0.4 }}
                disabled={textConfirmacio !== 'ELIMINAR'}
                onClick={handleEliminarTotesLesDades}
              >
                🗑️ Eliminar tot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function InfoFila({ label, value, last = false }) {
  return (
    <div style={{ ...S.fila, borderBottom: last ? 'none' : undefined }}>
      <span style={S.filaLabel}>{label}</span>
      <span style={S.filaValor}>{value}</span>
    </div>
  )
}

const S = {
  // ── Zona de perill ─────────────────────────────────────────────────────────
  zonaPerill:          { marginTop: 32, paddingTop: 24, borderTop: '2px solid #FF3B30' },
  periллDesc:          { fontSize: 13, color: '#6b7280', lineHeight: 1.6, margin: '8px 0 16px' },
  btnEliminar:         { width: '100%', backgroundColor: '#FF3B30', color: '#fff', border: 'none', borderRadius: 10, padding: '13px', fontSize: 15, fontWeight: 500, cursor: 'pointer' },
  // ── Modal eliminació ────────────────────────────────────────────────────────
  modalOverlay:        { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modalBox:            { backgroundColor: '#fff', borderRadius: 16, padding: 24, maxWidth: 340, width: '100%' },
  modalTitol:          { margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: '#FF3B30' },
  modalLlista:         { fontSize: 13, color: '#444', margin: '0 0 16px', paddingLeft: 20, lineHeight: 2 },
  modalAvis:           { backgroundColor: '#FFF3F3', border: '1px solid #FF3B30', borderRadius: 8, padding: 12, fontSize: 13, color: '#CC0000' },
  btnCancelModal:      { flex: 1, padding: 12, borderRadius: 10, border: '1px solid #e5e7eb', backgroundColor: '#fff', cursor: 'pointer', fontSize: 14 },
  btnConfirmarEliminar:{ flex: 1, padding: 12, borderRadius: 10, border: 'none', backgroundColor: '#FF3B30', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 },

  page:        { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#f9fafb' },
  header:      { padding: '14px 16px 10px', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb' },
  title:       { margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' },
  contingut:   { flex: 1, overflowY: 'auto', padding: '16px' },
  seccio:      { marginBottom: 20 },
  seccioTitol: { fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px 4px' },
  bloc:        { backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' },
  fila:        { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #f3f4f6' },
  filaLabel:   { fontSize: 15, color: '#111827' },
  filaValor:   { fontSize: 14, color: '#6b7280' },
  inputInline: { padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, outline: 'none', backgroundColor: '#fafafa' },
  tipusBtn:    { border: 'none', borderRadius: 8, padding: '6px 10px', fontSize: 16, cursor: 'pointer' },
  hint:        { fontSize: 12, color: '#9ca3af', margin: '6px 0 0 4px' },
  btnPrimari:  { backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 10, padding: '13px', width: '100%', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 20 },
  geocodingFila: { display: 'flex', gap: 6, width: '100%' },
  geoBtn:      { padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, backgroundColor: '#f0f9ff', cursor: 'pointer', fontSize: 16, flexShrink: 0 },
  geoError:    { fontSize: 12, color: '#ef4444' },
  geoOk:       { fontSize: 12, color: '#16a34a' },
}
